/**
 * MechaGuard - InputHandler (统一输入处理)
 *
 * 用 PointerEvent 同时覆盖鼠标 / 触摸 / 触控笔：
 *   - 单击 (tap/click)     -> onTap(worldX, worldY, event)
 *   - 悬停 (鼠标)          -> onHover(worldX, worldY)
 *   - 拖拽 (拖动 > 8px)    -> 平移相机
 *   - 双指捏合             -> 缩放相机
 *   - 滚轮                 -> 以指针为锚点缩放
 *   - 右键 / 长按(600ms)   -> onSecondary(worldX, worldY)
 *
 * 注意：早先版本把「按下到抬起间隔 < 350ms」判定为双击从而吞掉了普通
 * 快速点击，这里改为在 pointerup 时比较两次「成功的点击」时间戳。
 */

export class InputHandler {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {import('./Renderer.js').Renderer} renderer
   */
  constructor(canvas, renderer) {
    this.canvas = canvas;
    this.renderer = renderer;

    // ---- 单指/鼠标状态 ----
    this.downX = 0;
    this.downY = 0;
    this.lastX = 0;
    this.lastY = 0;
    this.isDragging = false;
    this.dragThreshold = 8; // px

    // ---- 多点触控（捏合缩放） ----
    /** @type {Map<number, {x:number, y:number}>} */
    this.pointers = new Map();
    this.pinchStartDist = 0;
    this.pinchStartZoom = 1;

    // ---- 长按 ----
    this.longPressTimer = 0;
    this.longPressDelay = 600;

    // ---- 双击 ----
    this.lastTapTime = 0;
    this.doubleTapDelay = 320;
    this.onDoubleTap = null;

    // ---- 回调（由 Game / Main 绑定） ----
    this.onTap = null;
    this.onHover = null;
    this.onSecondary = null;

    // 画布在页面中的位置（缓存，避免每次 move 都触发布局计算）
    this._rectLeft = 0;
    this._rectTop = 0;

    this._bind();
    this._updateRect();
  }

  /* ===================================================
   * 事件绑定
   * =================================================== */

  _bind() {
    const c = this.canvas;

    c.addEventListener('pointerdown', this._onPointerDown);
    c.addEventListener('pointermove', this._onPointerMove);
    c.addEventListener('pointerup', this._onPointerUp);
    c.addEventListener('pointercancel', this._onPointerUp);
    c.addEventListener('pointerleave', this._onPointerLeave);
    c.addEventListener('contextmenu', this._onContextMenu);
    c.addEventListener('wheel', this._onWheel, { passive: false });

    window.addEventListener('resize', this._updateRect);
    window.addEventListener('scroll', this._updateRect, true);
  }

  /** 解除所有监听（切换场景时调用） */
  destroy() {
    const c = this.canvas;
    c.removeEventListener('pointerdown', this._onPointerDown);
    c.removeEventListener('pointermove', this._onPointerMove);
    c.removeEventListener('pointerup', this._onPointerUp);
    c.removeEventListener('pointercancel', this._onPointerUp);
    c.removeEventListener('pointerleave', this._onPointerLeave);
    c.removeEventListener('contextmenu', this._onContextMenu);
    c.removeEventListener('wheel', this._onWheel);
    window.removeEventListener('resize', this._updateRect);
    window.removeEventListener('scroll', this._updateRect, true);
    this._clearLongPress();
  }

  /* ===================================================
   * 原生事件处理
   * =================================================== */

  _onPointerDown = (e) => {
    this._updateRect();

    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // 双指：进入捏合模式
    if (this.pointers.size === 2) {
      this._clearLongPress();
      this.isDragging = true; // 捏合期间不再触发 tap
      const [a, b] = [...this.pointers.values()];
      this.pinchStartDist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      this.pinchStartZoom = this.renderer.zoom;
      return;
    }

    if (this.pointers.size > 2) return;

    // 单指 / 鼠标
    this.downX = this.lastX = e.clientX;
    this.downY = this.lastY = e.clientY;
    this.isDragging = false;

    if (this.canvas.setPointerCapture) {
      try { this.canvas.setPointerCapture(e.pointerId); } catch { /* 忽略 */ }
    }

    if (e.pointerType !== 'mouse') {
      this._scheduleLongPress(e.clientX, e.clientY);
    }
  };

  _onPointerMove = (e) => {
    if (this.pointers.has(e.pointerId)) {
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    // ---- 双指捏合缩放 ----
    if (this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const midX = (a.x + b.x) / 2 - this._rectLeft;
      const midY = (a.y + b.y) / 2 - this._rectTop;

      const targetZoom = this.pinchStartZoom * (dist / this.pinchStartDist);
      const factor = targetZoom / this.renderer.zoom;
      if (Number.isFinite(factor) && factor > 0) {
        this.renderer.zoomAt(midX, midY, factor);
      }
      return;
    }

    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;

    if (!this.isDragging) {
      const movedX = Math.abs(e.clientX - this.downX);
      const movedY = Math.abs(e.clientY - this.downY);
      if (movedX > this.dragThreshold || movedY > this.dragThreshold) {
        this.isDragging = true;
        this._clearLongPress();
      }
    }

    if (this.isDragging) {
      this.renderer.panByScreen(dx, dy);
    } else if (e.pointerType === 'mouse' && this.onHover) {
      const [wx, wy] = this._toWorld(e.clientX, e.clientY);
      this.onHover(wx, wy);
    }

    this.lastX = e.clientX;
    this.lastY = e.clientY;
  };

  _onPointerUp = (e) => {
    this._clearLongPress();
    this.pointers.delete(e.pointerId);

    const wasDragging = this.isDragging;
    const multiTouch = this.pointers.size > 0;

    if (this.canvas.releasePointerCapture) {
      try { this.canvas.releasePointerCapture(e.pointerId); } catch { /* 忽略 */ }
    }

    // 捏合结束或拖拽结束：不产生点击
    if (wasDragging || multiTouch) {
      this.isDragging = false;
      return;
    }

    this.isDragging = false;

    const now = performance.now();
    const isDouble = now - this.lastTapTime < this.doubleTapDelay;

    const [wx, wy] = this._toWorld(e.clientX, e.clientY);

    // 仅当真正绑定了双击回调时，才把双击吞掉、不触发 onTap；
    // 否则（如本游戏）双击应降级为两次普通点击，避免快速双击丢第二下。
    if (isDouble && this.onDoubleTap) {
      this.lastTapTime = 0;
      this.onDoubleTap(wx, wy, e);
      return;
    }

    this.lastTapTime = now;
    if (this.onTap) this.onTap(wx, wy, e);
  };

  _onPointerLeave = (e) => {
    this._clearLongPress();
    this.pointers.delete(e.pointerId);
    this.isDragging = false;
  };

  _onContextMenu = (e) => {
    e.preventDefault(); // 屏蔽浏览器右键菜单
    if (this.onSecondary) {
      const [wx, wy] = this._toWorld(e.clientX, e.clientY);
      this.onSecondary(wx, wy);
    }
  };

  _onWheel = (e) => {
    e.preventDefault();
    const sx = e.clientX - this._rectLeft;
    const sy = e.clientY - this._rectTop;
    const factor = Math.pow(0.999, e.deltaY); // 平滑缩放
    this.renderer.zoomAt(sx, sy, factor);
  };

  /* ===================================================
   * 辅助
   * =================================================== */

  /**
   * 重新测量画布在页面中的位置。
   *
   * 注意：这里必须是「箭头类字段」而不是普通原型方法。
   * 它会作为 window 的 resize / scroll 监听器使用，普通方法的 this
   * 在事件触发时是 currentTarget（即 window），会直接抛
   * TypeError；类字段在构造函数体执行前就完成初始化，
   * 因此 this._bind() 注册时拿到的已经是绑定好 this 的函数。
   */
  _updateRect = () => {
    if (!this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    this._rectLeft = rect.left;
    this._rectTop = rect.top;
  };

  /** 客户端坐标 -> 世界坐标 */
  _toWorld(clientX, clientY) {
    const sx = clientX - this._rectLeft;
    const sy = clientY - this._rectTop;
    return this.renderer.screenToWorld(sx, sy);
  }

  _scheduleLongPress(clientX, clientY) {
    this._clearLongPress();
    this.longPressTimer = window.setTimeout(() => {
      this.longPressTimer = 0;
      if (this.isDragging) return;
      this.isDragging = true; // 消费掉随后的 pointerup，避免触发 tap
      if (this.onSecondary) {
        const [wx, wy] = this._toWorld(clientX, clientY);
        this.onSecondary(wx, wy);
      }
    }, this.longPressDelay);
  }

  _clearLongPress() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = 0;
    }
  }
}
